import { useEffect, useMemo, useState, type FormEvent } from "react";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, ExternalLink, LockKeyhole, Mail } from "lucide-react";
import { calculateQuote, formatMoney, type AcceptedQuote, type BusinessSettings, type Proposal, type ProposalSection } from "../../shared/types";
import { api } from "../api";
import { ThemeToggle } from "./ThemeToggle";

interface ProposalViewProps {
  proposal: Proposal;
  settings: BusinessSettings;
  preview?: boolean;
  onAccepted?: (acceptedAt: string, acceptedQuote: AcceptedQuote | null) => void;
}

function paragraphs(body: string) {
  return body.replace(/\\n/g, "\n").split(/\n{2,}/).filter(Boolean).map((paragraph, index) => <p key={`${paragraph.slice(0, 20)}-${index}`}>{paragraph}</p>);
}

function websiteLabel(url?: string) {
  try { return new URL(url ?? "").hostname.replace(/^www\./, ""); }
  catch { return "Website preview"; }
}

const paymentScheduleLabels: Record<Proposal["paymentSchedule"], string> = {
  full_upfront: "Full fee before work begins",
  half_upfront: "50% upfront, 50% on completion",
  milestones: "Paid in agreed milestones",
  custom: "Custom payment schedule",
};

function QuoteSummary({ quote }: { quote: AcceptedQuote }) {
  return <div className="proposal-quote-summary"><strong>{quote.optionLabel}</strong><p>{quote.optionDescription}</p><span>Total {formatMoney(quote.total)} · Due to begin {formatMoney(quote.dueNow)}</span></div>;
}

function SectionContent({ section, proposal, quote, onOptionChange }: { section: ProposalSection; proposal: Proposal; quote: AcceptedQuote | null; onOptionChange: (id: string) => void }) {
  if (section.type === "cards") {
    if (section.items.some((item) => item.url || item.imageUrl)) {
      return <div className="proposal-grid proposal-grid--work">{section.items.map((item, index) => (
        <article className="proposal-work-card" key={item.id}>
          {item.imageUrl && <div className="proposal-work-card__visual">
            <div className="proposal-work-card__window">
              <div className="proposal-work-card__browser" aria-hidden="true"><span className="proposal-work-card__dots"><i /><i /><i /></span><span>{websiteLabel(item.url)}</span><ArrowUpRight /></div>
              {item.url && /^https?:\/\//i.test(item.url) ? <a className="proposal-work-card__image-link" href={item.url} target="_blank" rel="noreferrer" aria-label={`Visit ${item.title} (opens in a new tab)`}><img src={item.imageUrl} alt={item.imageAlt || `Screenshot of the ${item.title} website`} width={1600} height={900} loading="lazy" decoding="async" /></a> : <img src={item.imageUrl} alt={item.imageAlt || `Screenshot of the ${item.title} website`} width={1600} height={900} loading="lazy" decoding="async" />}
            </div>
          </div>}
          <div className="proposal-work-card__content">
          <div className="proposal-work-card__top"><span>{item.meta || "Selected work"}</span><span aria-hidden="true">{String(index + 1).padStart(2, "0")}</span></div>
          <h3>{item.title}</h3>
          <div className="proposal-work-card__description">{paragraphs(item.body)}</div>
          {item.relevance && <div className="proposal-work-card__relevance"><h4>For {proposal.clientName}</h4>{paragraphs(item.relevance)}</div>}
          {item.url && /^https?:\/\//i.test(item.url) && <a className="proposal-work-card__link" href={item.url} target="_blank" rel="noreferrer" aria-label={`${item.linkLabel || "Visit website"}: ${item.title} (opens in a new tab)`}>{item.linkLabel || "Visit website"}<span><ArrowUpRight aria-hidden="true" /></span></a>}
          </div>
        </article>
      ))}</div>;
    }
    return <div className="proposal-grid proposal-grid--cards">{section.items.map((item) => (
      <article className="proposal-card" key={item.id}>
        {item.meta && <span className="proposal-card__meta">{item.meta}</span>}
        <h3>{item.title}</h3>
        {paragraphs(item.body)}
      </article>
    ))}</div>;
  }
  if (section.type === "list") {
    return <div className="proposal-list">{section.items.map((item) => (
      <article key={item.id}><span><Check /></span><div><h3>{item.title}</h3>{paragraphs(item.body)}</div></article>
    ))}</div>;
  }
  if (section.type === "timeline") {
    return <div className="proposal-timeline">{section.items.map((item) => (
      <article key={item.id}><strong>{item.meta || "Step"}</strong><div><h3>{item.title}</h3>{paragraphs(item.body)}</div></article>
    ))}</div>;
  }
  if (section.type === "investment") {
    const selectedQuote = section.pricing ? quote : null;
    const locked = Boolean(proposal.acceptedAt) || proposal.status === "accepted" || proposal.status === "paid";
    return <>
      {section.pricing && locked && selectedQuote ? <QuoteSummary quote={selectedQuote} /> : section.pricing && <fieldset className="proposal-options" disabled={locked}>
        <legend>{section.pricing.label}</legend>
        <p className="proposal-options__hint">{locked ? "Your agreed choice is recorded with this proposal." : "Choose one option. The setup fee is included in the total below and is payable upfront."}</p>
        <div className="proposal-options__grid">{section.pricing.options.map((option) => <label className={`proposal-option ${selectedQuote?.optionId === option.id ? "proposal-option--selected" : ""}`} key={option.id}>
          <input type="radio" name={`pricing-${section.id}`} value={option.id} checked={selectedQuote?.optionId === option.id} onChange={() => onOptionChange(option.id)} />
          <span><strong>{option.label}</strong><span className="proposal-option__price">{option.amount ? `+${formatMoney(option.amount)} setup` : "Setup included"}</span><span className="proposal-option__description">{option.description}</span></span>
        </label>)}</div>
      </fieldset>}
      <div className="proposal-investment">
      <div className="proposal-investment__terms">
        {paragraphs(section.body)}
        <dl>
          <div><dt>Payment schedule</dt><dd>{paymentScheduleLabels[proposal.paymentSchedule]}</dd></div>
          <div><dt>Payment method</dt><dd>{proposal.paymentMethod || "To be agreed"}</dd></div>
          {proposal.paymentNotes && <div><dt>Payment details</dt><dd>{proposal.paymentNotes}</dd></div>}
        </dl>
        {proposal.paymentLink && <a className="proposal-payment-link" href={proposal.paymentLink} target="_blank" rel="noreferrer">Open payment link <ExternalLink /></a>}
      </div>
      <aside aria-live="polite" aria-atomic="true"><small>{locked && selectedQuote ? "Agreed project investment" : "Total project investment"}</small><strong>{selectedQuote ? formatMoney(selectedQuote.total) : proposal.price || "Fee to be agreed"}</strong>
        {selectedQuote && <dl className="proposal-price-breakdown"><div><dt>Base project fee</dt><dd>{formatMoney(selectedQuote.baseAmount)}</dd></div><div><dt>{selectedQuote.optionLabel} setup</dt><dd>{selectedQuote.additionalAmount ? formatMoney(selectedQuote.additionalAmount) : "Included"}</dd></div></dl>}
        {(selectedQuote || proposal.amountDueToBegin) && <span><small>Due to begin</small><b>{selectedQuote ? formatMoney(selectedQuote.dueNow) : proposal.amountDueToBegin}</b></span>}
      </aside>
    </div></>;
  }
  return <div className="proposal-copy">{paragraphs(section.body)}</div>;
}

function Acceptance({ proposal, settings, preview, onAccepted, quote }: ProposalViewProps & { quote: AcceptedQuote | null }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState(proposal.clientEmail);
  const [note, setNote] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const accepted = Boolean(proposal.acceptedAt) || proposal.status === "accepted" || proposal.status === "paid";
  useEffect(() => { setAgreed(false); }, [quote?.optionId, quote?.total, quote?.dueNow]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (preview) return;
    setBusy(true);
    setError("");
    try {
      const result = await api<{ acceptedAt: string; acceptedQuote: AcceptedQuote | null }>(`/api/public/${proposal.slug}/accept`, {
        method: "POST",
        body: JSON.stringify({ name, email, note, agreed, optionId: quote?.optionId, expectedTotal: quote?.total, expectedDueNow: quote?.dueNow, proposalUpdatedAt: proposal.updatedAt }),
      });
      onAccepted?.(result.acceptedAt, result.acceptedQuote);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not accept the proposal.");
    } finally {
      setBusy(false);
    }
  };

  if (accepted) {
    return <div className="acceptance acceptance--complete"><span><Check /></span><div><strong>Proposal accepted</strong><p>Thank you. Leviathan will be in touch to arrange the next step.</p>{proposal.acceptedQuote && <QuoteSummary quote={proposal.acceptedQuote} />}</div></div>;
  }

  return <div className="acceptance">
    <div>
      <p className="proposal-eyebrow">Ready to proceed?</p>
      <h2>Accept this proposal.</h2>
      <p>Confirm below or contact <a href={`mailto:${settings.businessEmail}`}>{settings.businessEmail}</a> if you’d like to discuss anything first.</p>
      {quote && <QuoteSummary quote={quote} />}
    </div>
    {!open ? <button className="proposal-button" type="button" onClick={() => setOpen(true)} disabled={preview}>Accept proposal</button> : (
      <form onSubmit={submit}>
        <label>Your name<input value={name} onChange={(event) => setName(event.target.value)} required /></label>
        <label>Email address<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
        <label>Note <span>Optional</span><textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} /></label>
        <label className="acceptance-check"><input type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} required /><span>I confirm that I am authorised to accept this proposal and agree to its scope and payment terms.</span></label>
        {error && <p className="form-error">{error}</p>}
        <button className="proposal-button" type="submit" disabled={busy}>{busy ? "Accepting…" : "Confirm acceptance"}</button>
      </form>
    )}
  </div>;
}

export function ProposalView(props: ProposalViewProps) {
  const { proposal, settings, preview } = props;
  const pricing = proposal.sections.find((section) => section.type === "investment" && section.pricing)?.pricing;
  const [optionId, setOptionId] = useState("");
  const selectedId = pricing?.options.some((option) => option.id === optionId) ? optionId : pricing?.options[0]?.id ?? "";
  const quote = proposal.acceptedQuote ?? (pricing ? calculateQuote(pricing, selectedId) : null);
  const [active, setActive] = useState(proposal.sections[0]?.id ?? "");
  const activeIndex = useMemo(() => Math.max(0, proposal.sections.findIndex((section) => section.id === active)), [active, proposal.sections]);

  useEffect(() => {
    if (preview) return;
    const syncActiveSection = () => {
      const marker = Math.min(window.innerHeight * 0.3, 220);
      let current = proposal.sections[0]?.id ?? "";
      proposal.sections.forEach((section) => {
        const element = document.getElementById(`proposal-${section.id}`);
        if (element && element.getBoundingClientRect().top <= marker) current = section.id;
      });
      setActive(current);
    };
    syncActiveSection();
    window.addEventListener("scroll", syncActiveSection, { passive: true });
    window.addEventListener("resize", syncActiveSection);
    return () => {
      window.removeEventListener("scroll", syncActiveSection);
      window.removeEventListener("resize", syncActiveSection);
    };
  }, [preview, proposal.sections]);

  const move = (direction: number) => {
    const next = proposal.sections[Math.max(0, Math.min(proposal.sections.length - 1, activeIndex + direction))];
    document.getElementById(`proposal-${next.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActive(next.id);
  };

  return <div className={`proposal-document ${preview ? "proposal-document--preview" : ""}`} style={{ "--proposal-accent": proposal.accent } as React.CSSProperties}>
    {!preview && <aside className="proposal-side">
      <div className="proposal-brand"><span className="proposal-brand__mark"><i /></span><span>Leviathan<small>{proposal.clientName} proposal</small></span></div>
      <nav>{proposal.sections.map((section, index) => <a className={active === section.id ? "active" : ""} href={`#proposal-${section.id}`} key={section.id}><span>{String(index + 1).padStart(2, "0")}</span>{section.navLabel}</a>)}</nav>
      <ThemeToggle scope="proposal" />
      <a className="proposal-email" href={`mailto:${settings.businessEmail}`}><Mail />Contact Leviathan</a>
    </aside>}
    <div className="proposal-body">
      {!preview && <div className="proposal-mobile-nav">
        <button type="button" onClick={() => move(-1)} disabled={activeIndex === 0} aria-label="Previous section"><ArrowLeft /></button>
        <div><strong>{proposal.sections[activeIndex]?.navLabel}</strong><span>{String(activeIndex + 1).padStart(2, "0")} / {String(proposal.sections.length).padStart(2, "0")}</span></div>
        <button type="button" onClick={() => move(1)} disabled={activeIndex === proposal.sections.length - 1} aria-label="Next section"><ArrowRight /></button><ThemeToggle compact scope="proposal" />
      </div>}
      {proposal.sections.map((section, index) => {
        const isHero = section.type === "overview" && index === 0;
        return <section className={`proposal-section proposal-section--${section.type} ${isHero ? "proposal-hero" : ""}`} id={`proposal-${section.id}`} key={section.id}>
          <div className="proposal-wrap">
            <p className="proposal-eyebrow">{section.eyebrow || proposal.projectLabel}</p>
            <h1>{isHero ? proposal.title : section.title}</h1>
            {isHero && <p className="proposal-intro">{proposal.intro || section.body}</p>}
            {isHero && proposal.heroImage && <img className="proposal-hero-image" src={proposal.heroImage} alt="" />}
            {!isHero && ["cards", "list", "timeline"].includes(section.type) && section.body && <div className="proposal-copy proposal-section-intro">{paragraphs(section.body)}</div>}
            {!isHero && <SectionContent section={section} proposal={proposal} quote={quote} onOptionChange={setOptionId} />}
            {section.type === "cta" && <Acceptance {...props} quote={quote} />}
          </div>
        </section>;
      })}
      <footer className="proposal-footer"><p>Prepared for {proposal.clientName} by {settings.businessName}.</p><p>{settings.businessEmail} · {settings.businessPhone}</p></footer>
    </div>
  </div>;
}

export function ProposalLock({ clientName, title, slug, accent, onUnlocked }: { clientName: string; title: string; slug: string; accent?: string; onUnlocked: () => void }) {
  const [accessCode, setAccessCode] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api(`/api/public/${slug}/unlock`, { method: "POST", body: JSON.stringify({ accessCode }) });
      onUnlocked();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not open this proposal.");
    } finally { setBusy(false); }
  };
  return <main className="proposal-lock" style={accent ? { "--proposal-accent": accent } as React.CSSProperties : undefined}>
    <div className="proposal-lock__card">
      <span className="proposal-lock__mark"><LockKeyhole /></span>
      <p className="proposal-eyebrow">Private proposal · {clientName}</p>
      <h1>{title}</h1>
      <p>Enter the access code supplied by Leviathan to continue.</p>
      <form onSubmit={submit}>
        <label>Access code<div className="password-input"><input type={show ? "text" : "password"} value={accessCode} onChange={(event) => setAccessCode(event.target.value)} autoFocus required /><button type="button" onClick={() => setShow(!show)}>{show ? "Hide" : "Show"}</button></div></label>
        {error && <p className="form-error">{error}</p>}
        <button className="proposal-button" type="submit" disabled={busy}>{busy ? "Opening…" : "Open proposal"}</button>
      </form>
    </div>
  </main>;
}
